import type { DownloadLinks, PlatformId } from "../data/siteContent";

export type OsId = "windows" | "macos" | "linux" | "unknown";
export type CpuArch = "x64" | "arm64" | "unknown";

type NavigatorHints = {
  userAgent: string;
  maxTouchPoints?: number;
};

export function detectOs(userAgent: string): OsId {
  const ua = userAgent.toLowerCase();
  if (ua.includes("win")) return "windows";
  if (ua.includes("mac")) return "macos";
  if (ua.includes("linux")) return "linux";
  return "unknown";
}

/** Best-effort CPU arch from UA; macOS Apple Silicon often masquerades as MacIntel */
export function detectCpuArch(userAgent: string, maxTouchPoints = 0): CpuArch {
  const ua = userAgent.toLowerCase();
  if (ua.includes("aarch64") || ua.includes("arm64")) return "arm64";
  if (ua.includes("x86_64") || ua.includes("amd64") || ua.includes("win64")) {
    return "x64";
  }
  if (/armv\d+/u.test(ua)) return "arm64";

  if (ua.includes("mac")) {
    return maxTouchPoints > 1 ? "arm64" : "x64";
  }

  return "unknown";
}

export function resolveDownloadHref(
  os: OsId,
  links: DownloadLinks,
  hints: NavigatorHints,
): string {
  const arch = detectCpuArch(hints.userAgent, hints.maxTouchPoints ?? 0);

  if (os === "windows") return links.windows;
  if (os === "macos") {
    return arch === "x64" ? links.macos.x64 : links.macos.arm64;
  }
  if (os === "linux") {
    return arch === "arm64" ? links.linux.arm64 : links.linux.x64;
  }
  return links.windows;
}

/** Per-platform card button: picks arch when visitor is on that OS, else sensible default */
export function resolvePlatformDownloadHref(
  platform: PlatformId,
  links: DownloadLinks,
  hints: NavigatorHints,
): string {
  if (platform === "windows") return links.windows;

  const os = detectOs(hints.userAgent);
  const arch = detectCpuArch(hints.userAgent, hints.maxTouchPoints ?? 0);

  if (platform === "macos") {
    if (os === "macos") {
      return arch === "x64" ? links.macos.x64 : links.macos.arm64;
    }
    return links.macos.arm64;
  }

  if (os === "linux" && arch === "arm64") return links.linux.arm64;
  return links.linux.x64;
}
